import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import Plot from 'react-plotly.js';
import {
  ManRadioButtonGroup,
  ManRadioButton,
  ManText
} from '@craft-design-system/core-components-react';
import Shimmer_graph from '../shimmer_graph';
import ifav from './ifav.png';
import { CustomTextDisplay } from '../AI';
import AIToggleButton from '../AIToggleButton'; // 🔥 Added AI Toggle Button
import { useResetContext } from '../ResetContext';
import { useFilterContext } from '../FilterContext';
import useGraphRadio from '../../utils/useGraphRadio';
import Zoom from '../Zoom';
import useSegmentedChartQuery from '../../utils/useSegmentedChartQuery';
import AverageDisplay from '../ride_analysis_kpi/Average_value';
import DownloadButtonComponent from '../DownloadButtonComponent';
import SegmentedControl from '../ride_analysis_kpi/SegmentedControl';

const Charging_power = ({ value }) => {
  const [charging_power, set_charging_power] = useState(null);
  const [downloadData, setDownloadData] = useState([]);
  const [radio_buttons_test, set_Radio_values] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { filterdata } = useFilterContext();
  const { startDate, endDate } = filterdata || {};
  const [showMonthlyText, setShowMonthlyText] = useState(value.system_charging_power_kw[1]['average_power']);
  const [avg_value, setavg_value] = useState(value.system_charging_power_kw[2]['average']);
  const [chartType, setChartType] = useState("Bar");
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { fetchSegmentedChart } = useSegmentedChartQuery();
  
  // 🔥 AI-related state
  const [cleanedAIData, setCleanedAIData] = useState([]);
  const [radioButtons, setRadioButtons] = useState({
    'Charging Power': 'Daily'
  });
  
  const { resetdata } = useResetContext();
  const radio_query = useGraphRadio();

  // 🔥 Clean download data for AI insights
  useEffect(() => {
    if (downloadData && downloadData.length > 0) {
      const cleanData = downloadData.filter(item => 
        item && typeof item === 'object' && Object.keys(item).length > 0
      );
      setCleanedAIData(cleanData);
    }
  }, [downloadData]);

  const popover = (
    <Popover id="popover-basic">
      <Popover.Body>
        <strong>Definition:</strong><br />
        The charging power is displayed by two values: maximum value during charging cycle and average value during charging cycle.<br /><br />
  
        <strong>Telematic raw data for this KPI:</strong><br />
        TBM3:<br />
        • chargedenergy   <br />
        • energylevel     <br />
        • signal_timestamp<br />
        CM4:<br />
        • chargedenergy   <br />
        • batchgdenergycbp<br />
        • energylevel     <br />
        • energylevelcbp  <br />
        • signal_timestamp<br /><br />
  
        <strong>Time series data:</strong><br />
        If multiple vehicles are selected, average values are displayed. Days without data are not taken into account for the calculation.<br /><br />
  
        <strong>Histogram:</strong><br />
        Each bar represents the number of days that fall within a certain range (bin) of <strong>daily average cycle charging power values.</strong><br />
        All daily average cycle charging power values since start of data acquisition for the selected vehicle(s) are visualized per default.<br /><br />
  
        <strong>Boxplot:</strong><br />
        The boxplot shows the <strong>distribution of daily average cycle charging power values</strong> for the selected vehicle(s) since start of data acquisition.<br />
        If multiple vehicles are selected, the boxplot reflects the combined distribution of all their daily values.<br />
        • The <strong>box</strong> represents the middle 50% of values (from the 25th percentile ("q1") to the 75th percentile ("q3")).<br />
        • The line <strong>inside the box</strong> shows the median (50th percentile).<br />
        • The whiskers extend to show the range of typical values ("min" and "max") and visualize <strong>the spread of the data</strong> beyond the central box.
      </Popover.Body>
    </Popover>
  );

  const handleSelectChange1 = (event) => {
    console.log('📻 Radio button changed:', event.detail.name, event.detail.value);
    
    const newRadioValues = {
      ...radio_buttons_test,
      [event.detail.name]: event.detail.value,
    };
    set_Radio_values(newRadioValues);
    
    // 🔥 Update radio buttons state for AI insights
    setRadioButtons(newRadioValues);
  };

  // 🔥 Fetch data when chart type changes
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        console.log('📊 Fetching segmented chart with category:', resetdata);
        
        const data = await fetchSegmentedChart({
          vinShort: value.vin_short,
          chartType,
          category: resetdata,
          columnname: "Charging power",
        });

        if (chartType === 'Bar') {
          set_charging_power(JSON.parse(data[0]));
          setDownloadData(JSON.parse(data[3]));
        } else {
          set_charging_power(JSON.parse(data[0]));
          setDownloadData(JSON.parse(data[1]));
        }
      } catch (error) {
        console.error('Error fetching chart data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isInitialLoad && chartType && value?.vin_short) {
      fetchData();
    } else {
      setIsInitialLoad(false);
    }
  }, [chartType, resetdata]);

  // 🔥 Handle radio button changes - enhanced like other components
  useEffect(() => {
    if (!radio_buttons_test) return;

    console.log('📻 Radio fetch triggered with:', radio_buttons_test);
    console.log('📻 Current category (resetdata):', resetdata);

    setIsLoading(true);

    // 🔥 Enhanced params to include category properly
    const params = {
      vins_short: [value.vin_short],
      radio_value: radio_buttons_test,
      columnname: 'Charging power',
      category: resetdata,
      startDate: startDate,
      endDate: endDate,
      filterdata: {
        ...filterdata,
        category: resetdata,
        resetdata: resetdata
      },
      resetdata: resetdata,
      chartType,
    };

    console.log('📻 Sending radio query with params:', params);

    radio_query(params)
      .then(res => {
        console.log('📻 Radio query response:', res);

        // 🔥 Support BOTH response formats
        const payload = res.jsonData ?? res;
        
        if (payload) {
          set_charging_power(JSON.parse(payload[0]));
          setShowMonthlyText(payload[1]['average_power']);
          setavg_value(payload[2]['average']);
          setDownloadData(JSON.parse(payload[3]));
        }
      })
      .catch(err => {
        console.error("❌ Radio fetch failed:", err);
      })
      .finally(() => setIsLoading(false));

  }, [radio_buttons_test, resetdata, chartType]);

  // 🔥 Initialize data from props
  useEffect(() => {
    try {
      set_charging_power(JSON.parse(value.system_charging_power_kw[0]));
      if (value.system_charging_power_kw[3]) {
        setDownloadData(JSON.parse(value.system_charging_power_kw[3]));
      }
    } catch (error) {
      console.error("Error parsing JSON", error);
    }
  }, [value]);

  // 🔥 Monitor resetdata changes
  useEffect(() => {
    console.log('🔄 resetdata changed to:', resetdata);
    
    // If we have radio buttons selected and resetdata changes, refetch
    if (radio_buttons_test && resetdata) {
      console.log('🔄 Refetching due to category change');
      // Trigger refetch by updating radio buttons state slightly
      set_Radio_values({...radio_buttons_test});
    }
  }, [resetdata]);

  return (
    <div style={{ padding: '16px' }}>
      {/* Debug info - remove in production */}
      {/* <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
        Debug: Category = {resetdata || 'None'}, Radio = {JSON.stringify(radio_buttons_test)}
      </div> */}

      {/* Title and Controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '8px',
        gap: '8px'
      }}>
        <div style={{
          flex: 1,
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: "120px"
        }}>
          <ManText
            className="card-title-text"
            variant="component-l"
            as="span"
            size="l"
            style={{ lineHeight: 'normal' }}
          >
            System charging power (cycle)
          </ManText>
        </div>
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          flexDirection: "row-reverse",
          gap: 10 // 🔥 Added gap for consistent spacing
        }}>
          <OverlayTrigger trigger="hover" placement="left" overlay={popover}>
            <img 
              className="man-logovv1" 
              src={ifav} 
              alt="info" 
              style={{ 
                cursor: 'pointer',
                alignSelf: 'center' // 🔥 Ensures info icon aligns with other icons
              }} 
            />
          </OverlayTrigger>
          <Zoom
            chartData={charging_power?.data}
            chartLayout={charging_power?.layout}
            chartTitle="System charging power (cycle)"
          />
          <DownloadButtonComponent
            data={downloadData}
            fileName={`System_charging_power_${chartType.toLowerCase()}_${radio_buttons_test?.['Charging Power']?.toLowerCase() || 'daily'}.xlsx`}
            disabled={!downloadData || downloadData.length === 0}
          />
        </div>
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '20px',
        marginTop: '16px',
      }}>
        {/* Average Value Display (only for Bar) */}
        {chartType === 'Bar' ? (
          <div style={{ flexShrink: 0 }}>
            <AverageDisplay averages={avg_value} unit="kW" />
          </div>
        ) : (
          <div style={{ width: '150px' }} />
        )}

        {/* Radio Buttons */}
        {chartType === 'Bar' ? (
          <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
            <ManRadioButtonGroup
              name="Charging Power"
              invalid={false}
              onManChange={handleSelectChange1}
              style={{ display: 'flex', gap: '20px' }}
            >
              <ManRadioButton name="Charging Power" label="Daily" value="Daily" checked />
              <ManRadioButton name="Charging Power" label="Monthly" value="Monthly" />
              <ManRadioButton name="Charging Power" label="Yearly" value="Yearly" />
            </ManRadioButtonGroup>
          </div>
        ) : (
          <div style={{ flexGrow: 1 }} />
        )}

        {/* Chart Type Selector */}
        <div style={{ flexShrink: 0 }}>
          <SegmentedControl
            selected={chartType}
            onChange={(type) => setChartType(type)}
            options={["Bar", "Histogram", "Boxplot"]}
          />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      {isLoading ? (
        <Shimmer_graph />
      ) : (
        charging_power && (
          <>
            <Plot
              data={charging_power.data}
              layout={{
                ...charging_power.layout,
                margin: chartType === 'Bar' 
                  ? { t: 10, r: 10, l: 10, b: 160 } // 🔥 Reduced margin since button is moved outside
                  : { t: 10, r: 10, l: 10, b: 180 },
                autosize: true,
                responsive: true,
                height: chartType === 'Bar' ? 450 : 400,
                xaxis: {
                  ...charging_power.layout?.xaxis,
                  rangeslider: { visible: false },
                },
              }}
              useResizeHandler={true}
              style={{ 
                width: '100%', 
                height: chartType === 'Bar' ? '450px' : '400px'
              }}
            />

            {/* 🔥 AI Toggle Button - Bottom left corner */}
            {chartType === 'Bar' && cleanedAIData?.length > 0 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-start', // 🔥 Left alignment
                marginTop: '16px' // 🔥 Space from chart
              }}>
                <AIToggleButton
                  downloadData={cleanedAIData}
                  radioButtonState={radioButtons}
                  kpiName="Charging Power"
                  chartType={chartType}
                  vinShort={value.vin_short}
                  title="AI | Automotive Intelligence - System Charging Power Analysis"
                />
              </div>
            )}
          </>
        )
      )}
    </div>
    </div>
  );
};

Charging_power.propTypes = {
  value: PropTypes.shape({
    vin_short: PropTypes.string.isRequired,
    system_charging_power_kw: PropTypes.array.isRequired,
  }).isRequired,
};

export default Charging_power;